import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { clientApi, getApiErrorMessage } from "@/lib/api";
import { ClientLayout } from "@/components/layout/ClientLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { formatCurrency } from "@/lib/format";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { toast } from "sonner";
import { TrendingUp, TrendingDown, Wallet, Percent, IndianRupee, FileUp, XCircle } from "lucide-react";

const COLORS = ["#0b5c46", "#0a9970", "#63b3a0", "#b7dfd2", "#e8a33d", "#7a5195", "#374c80", "#bc5090"];

const fmtPct = (v) => (v == null ? "—" : `${v > 0 ? "+" : ""}${v}%`);
const gainClass = (v) => (v >= 0 ? "text-emerald-700" : "text-red-600");

function SummaryCards({ s }) {
  const cards = [
    { label: "Current Value", value: formatCurrency(s?.current_value), sub: `Day: ${s?.day_change >= 0 ? "+" : ""}${formatCurrency(s?.day_change)} (${fmtPct(s?.day_change_pct)})`, subClass: gainClass(s?.day_change ?? 0), icon: Wallet, testid: "pf-current-value" },
    { label: "Invested", value: formatCurrency(s?.invested), sub: `${s?.schemes_count ?? 0} schemes`, icon: IndianRupee, testid: "pf-invested" },
    { label: "Overall Gain", value: `${s?.gain >= 0 ? "+" : ""}${formatCurrency(s?.gain)}`, sub: fmtPct(s?.gain_pct), valueClass: gainClass(s?.gain ?? 0), subClass: gainClass(s?.gain ?? 0), icon: s?.gain >= 0 ? TrendingUp : TrendingDown, testid: "pf-gain" },
    { label: "XIRR (Active)", value: fmtPct(s?.active_xirr), sub: `Lifetime: ${fmtPct(s?.lifetime_xirr)}`, valueClass: gainClass(s?.active_xirr ?? 0), icon: Percent, testid: "pf-xirr" },
  ];
  return (
    <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {cards.map((c) => (
        <Card key={c.label} className="glass-card border-0">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 text-sm text-[var(--ink-muted)]"><c.icon className="h-4 w-4" /> {c.label}</div>
            <div className={`mt-2 font-heading text-2xl text-[var(--brand)] ${c.valueClass || ""}`} data-testid={c.testid}>{c.value}</div>
            <div className={`mt-1 text-xs text-[var(--ink-muted)] ${c.subClass || ""}`}>{c.sub}</div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function AllocationDonut({ allocation }) {
  const data = allocation?.by_asset_class || [];
  const total = data.reduce((s, d) => s + d.value, 0);
  return (
    <Card className="glass-card border-0">
      <CardHeader><CardTitle className="font-heading text-base text-[var(--brand)]">Asset Allocation</CardTitle></CardHeader>
      <CardContent className="flex flex-col items-center gap-4 sm:flex-row">
        <div className="h-44 w-44 shrink-0">
          <ResponsiveContainer width="100%" height={176} minWidth={0}>
            <PieChart>
              <Pie data={data} dataKey="value" nameKey="name" innerRadius={48} outerRadius={72} paddingAngle={2}>
                {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v) => formatCurrency(v)} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="w-full min-w-0 flex-1 space-y-2">
          {data.map((d, i) => (
            <div key={d.name} className="flex items-center justify-between text-xs" data-testid={`alloc-${d.name.toLowerCase()}`}>
              <span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ background: COLORS[i % COLORS.length] }} /> {d.name}</span>
              <span className="font-medium">{total ? ((d.value / total) * 100).toFixed(1) : 0}%</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function CategoryBars({ allocation, total }) {
  const data = allocation?.by_sub_category || [];
  return (
    <Card className="glass-card border-0">
      <CardHeader><CardTitle className="font-heading text-base text-[var(--brand)]">Category Exposure</CardTitle></CardHeader>
      <CardContent className="space-y-2.5">
        {data.map((d, i) => (
          <div key={d.name}>
            <div className="flex justify-between text-xs"><span>{d.name}</span><span className="text-[var(--ink-muted)]">{formatCurrency(d.value)}</span></div>
            <div className="mt-1 h-1.5 rounded-full bg-[var(--surface-2)]">
              <div className="h-1.5 rounded-full" style={{ width: `${total ? (d.value / total) * 100 : 0}%`, background: COLORS[i % COLORS.length] }} />
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function HoldingsTable({ holdings }) {
  if (!holdings.length) return <div className="p-6 text-sm text-[var(--ink-muted)]" data-testid="no-holdings-message">No holdings yet. Your allotted orders will appear here.</div>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[var(--glass-border)] text-left text-xs text-[var(--ink-muted)]">
            <th className="p-3 font-medium">Scheme</th>
            <th className="p-3 font-medium">Folio / mode</th>
            <th className="p-3 text-right font-medium">Units</th>
            <th className="p-3 text-right font-medium">Avg NAV</th>
            <th className="p-3 text-right font-medium">NAV</th>
            <th className="p-3 text-right font-medium">Invested</th>
            <th className="p-3 text-right font-medium">Value</th>
            <th className="p-3 text-right font-medium">Day Chg</th>
            <th className="p-3 text-right font-medium">Gain</th>
            <th className="p-3 text-right font-medium">XIRR</th>
            <th className="p-3 text-right font-medium">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[var(--glass-border)]">
          {holdings.map((h) => (
            <tr key={h.holding_key || h.scheme_code} data-testid={`holding-row-${h.scheme_code}`}>
              <td className="max-w-[280px] p-3">
                <Link to={`/funds/${h.scheme_code}`} className="font-medium text-[var(--brand)] hover:underline">{h.scheme_name}</Link>
                <div className="text-xs text-[var(--ink-muted)]">{h.sub_category} · {h.asset_class}</div>
              </td>
              <td className="p-3 text-xs"><div>{h.folio_no || "New folio"}</div><div className="text-[var(--ink-muted)]">{h.holding_mode || "PHYSICAL"} · {(h.sources || []).join(" + ") || "BSE"}</div></td>
              <td className="p-3 text-right font-mono-app">{h.units}</td>
              <td className="p-3 text-right">{h.avg_nav?.toFixed(2)}</td>
              <td className="p-3 text-right">{h.nav?.toFixed(2)}</td>
              <td className="p-3 text-right">{formatCurrency(h.invested)}</td>
              <td className="p-3 text-right font-medium">{formatCurrency(h.current_value)}</td>
              <td className={`p-3 text-right ${gainClass(h.day_change)}`}>{fmtPct(h.day_change_pct)}</td>
              <td className={`p-3 text-right font-medium ${gainClass(h.gain)}`}>{formatCurrency(h.gain)}<div className="text-xs">{fmtPct(h.gain_pct)}</div></td>
              <td className={`p-3 text-right font-medium ${gainClass(h.xirr ?? 0)}`}>{fmtPct(h.xirr)}</td>
              <td className="p-3 text-right text-xs"><div className="flex justify-end gap-2"><Link className="font-medium text-[var(--cta)] hover:underline" to={`/invest/${h.scheme_code}`}>Buy</Link><Link className="font-medium text-[var(--cta)] hover:underline" to={`/redeem?holding=${encodeURIComponent(h.holding_key || h.scheme_code)}`}>Sell</Link></div></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CapitalGains() {
  const [data, setData] = useState(null);
  const [fy, setFy] = useState(() => {
    const now = new Date();
    const y = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
    return `${y}-${String(y + 1).slice(2)}`;
  });

  useEffect(() => {
    clientApi.get(`/portfolio/capital-gains?fy=${fy}`).then((r) => setData(r.data));
  }, [fy]);

  const fyOptions = [];
  const nowY = new Date().getFullYear();
  for (let y = nowY; y >= nowY - 4; y--) fyOptions.push(`${y}-${String(y + 1).slice(2)}`);

  const t = data?.totals;
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <span className="text-sm text-[var(--ink-muted)]">Financial year</span>
        <select className="rounded-md border border-[var(--glass-border)] bg-white p-2 text-sm" value={fy} onChange={(e) => setFy(e.target.value)} data-testid="cg-fy-select">
          {fyOptions.map((f) => <option key={f} value={f}>{`FY ${f}`}</option>)}
        </select>
      </div>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {[["Realized STCG", t?.realized_stcg], ["Realized LTCG", t?.realized_ltcg], ["Unrealized STCG", t?.unrealized_stcg], ["Unrealized LTCG", t?.unrealized_ltcg]].map(([label, v]) => (
          <Card key={label} className="border-[var(--glass-border)]">
            <CardContent className="p-4">
              <div className="text-xs text-[var(--ink-muted)]">{label}</div>
              <div className={`mt-1 font-heading text-lg ${gainClass(v ?? 0)}`} data-testid={`cg-${label.toLowerCase().replace(/ /g, "-")}`}>{formatCurrency(v)}</div>
            </CardContent>
          </Card>
        ))}
      </div>
      <Card className="border-[var(--glass-border)]">
        <CardHeader><CardTitle className="font-heading text-base text-[var(--brand)]">Realized gains (FY {fy})</CardTitle></CardHeader>
        <CardContent className="p-0">
          {!data?.realized?.length ? <div className="p-5 text-sm text-[var(--ink-muted)]" data-testid="no-realized-gains">No redemptions in this financial year.</div> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-[var(--glass-border)] text-left text-xs text-[var(--ink-muted)]">
                  <th className="p-3 font-medium">Scheme</th><th className="p-3 text-right font-medium">Units</th><th className="p-3 font-medium">Buy</th><th className="p-3 font-medium">Sell</th><th className="p-3 text-right font-medium">Cost</th><th className="p-3 text-right font-medium">Proceeds</th><th className="p-3 text-right font-medium">Gain</th><th className="p-3 font-medium">Term</th>
                </tr></thead>
                <tbody className="divide-y divide-[var(--glass-border)]">
                  {data.realized.map((r, i) => (
                    <tr key={i}>
                      <td className="max-w-[240px] p-3 text-xs">{r.scheme_name}</td>
                      <td className="p-3 text-right">{r.units}</td>
                      <td className="p-3 text-xs">{r.buy_date}</td>
                      <td className="p-3 text-xs">{r.sell_date}</td>
                      <td className="p-3 text-right">{formatCurrency(r.cost)}</td>
                      <td className="p-3 text-right">{formatCurrency(r.proceeds)}</td>
                      <td className={`p-3 text-right font-medium ${gainClass(r.gain)}`}>{formatCurrency(r.gain)}</td>
                      <td className="p-3 text-xs">{r.term}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function HealthCard() {
  const [health, setHealth] = useState(null);
  useEffect(() => { clientApi.get("/portfolio/health").then((r) => setHealth(r.data)).catch(() => {}); }, []);
  if (!health || !health.components?.length) return null;
  const color = health.score >= 85 ? "#0a9970" : health.score >= 70 ? "#65a30d" : health.score >= 50 ? "#e8a33d" : "#dc2626";
  const r = 40, c = 2 * Math.PI * r;
  return (
    <Card className="glass-card mb-6 border-0">
      <CardContent className="flex flex-col gap-5 p-5 lg:flex-row lg:items-center">
        <div className="flex shrink-0 flex-col items-center">
          <svg width="110" height="110" viewBox="0 0 110 110">
            <circle cx="55" cy="55" r={r} fill="none" stroke="var(--surface-2)" strokeWidth="10" />
            <circle cx="55" cy="55" r={r} fill="none" stroke={color} strokeWidth="10" strokeLinecap="round"
              strokeDasharray={c} strokeDashoffset={c * (1 - health.score / 100)} transform="rotate(-90 55 55)" />
            <text x="55" y="52" textAnchor="middle" fontSize="24" fontWeight="700" fill="var(--brand)" data-testid="health-score">{health.score}</text>
            <text x="55" y="70" textAnchor="middle" fontSize="10" fill="var(--ink-muted)">/ 100</text>
          </svg>
          <div className="mt-1 text-sm font-semibold" style={{ color }} data-testid="health-grade">{health.grade}</div>
          <div className="text-[10px] uppercase tracking-wide text-[var(--ink-muted)]">Portfolio Health</div>
        </div>
        <div className="grid flex-1 gap-2 sm:grid-cols-2">
          {health.components.map((comp) => (
            <div key={comp.name} className="rounded-xl border border-[var(--glass-border)] p-3" data-testid={`health-${comp.name.toLowerCase().replace(/ /g, "-")}`}>
              <div className="flex items-center justify-between text-xs">
                <span className="font-semibold text-[var(--brand)]">{comp.name}</span>
                <span className="text-[var(--ink-muted)]">{comp.score}/{comp.max}</span>
              </div>
              <div className="mt-1.5 h-1.5 rounded-full bg-[var(--surface-2)]">
                <div className="h-1.5 rounded-full" style={{ width: `${(comp.score / comp.max) * 100}%`, background: comp.score / comp.max >= 0.8 ? "#0a9970" : comp.score / comp.max >= 0.5 ? "#e8a33d" : "#dc2626" }} />
              </div>
              <p className="mt-1.5 text-[11px] leading-snug text-[var(--ink-muted)]">{comp.advice}</p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function ExternalHoldings() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => clientApi.get("/external-holdings").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const importCas = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const text = await file.text();
      const { data: res } = await clientApi.post("/external-holdings/import-cas", { file_content: text });
      toast.success(`Imported ${res.imported} holdings${res.skipped?.length ? ` · skipped ${res.skipped.length}` : ""}`);
      load();
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const remove = async (code) => { await clientApi.delete(`/external-holdings/${code}`); load(); };
  const t = data?.totals;

  return (
    <Card className="glass-card mt-6 border-0">
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <CardTitle className="font-heading text-base text-[var(--brand)]">External Investments (holding-away)</CardTitle>
            <p className="mt-1 text-xs text-[var(--ink-muted)]">Import your CAS summary CSV (SCHEME_NAME/AMFI_CODE/ISIN, FOLIO, UNITS, COST) to consolidate investments held outside SMIFS.</p>
          </div>
          <label className={`flex cursor-pointer items-center gap-1.5 rounded-full bg-[var(--cta)] px-4 py-2 text-xs font-medium text-white ${busy ? "opacity-60" : ""}`}>
            <FileUp className="h-3.5 w-3.5" /> {busy ? "Importing..." : "Import CAS CSV"}
            <input type="file" accept=".csv,.txt" className="hidden" onChange={importCas} disabled={busy} data-testid="cas-import-input" />
          </label>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {!data?.holdings?.length ? (
          <div className="px-5 pb-5 text-sm text-[var(--ink-muted)]" data-testid="no-external-holdings">No external holdings yet.</div>
        ) : (
          <>
            <div className="flex flex-col gap-2 border-b border-[var(--glass-border)] px-4 pb-3 text-sm sm:flex-row sm:gap-6 sm:px-5">
              <div><span className="text-xs text-[var(--ink-muted)]">External value </span><span className="font-semibold" data-testid="external-total-value">{formatCurrency(t?.current_value)}</span></div>
              {t?.gain != null && <div className={gainClass(t.gain)}><span className="text-xs text-[var(--ink-muted)]">Gain </span><span className="font-semibold">{formatCurrency(t.gain)}</span></div>}
            </div>
            <div className="divide-y divide-[var(--glass-border)]">
              {data.holdings.map((h) => (
                <div key={`${h.scheme_code}-${h.folio}`} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 text-sm sm:flex-nowrap sm:px-5" data-testid={`external-row-${h.scheme_code}`}>
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium">{h.scheme_name}</div>
                    <div className="text-xs text-[var(--ink-muted)]">Folio {h.folio || "—"} · {h.units} units @ ₹{h.nav?.toFixed(2)} · {h.source}</div>
                  </div>
                  <div className="text-right font-medium">{formatCurrency(h.current_value)}</div>
                  <button onClick={() => remove(h.scheme_code)} className="text-red-500 hover:text-red-700" data-testid={`external-remove-${h.scheme_code}`}><XCircle className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default function Portfolio() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    clientApi.get("/portfolio/holdings").then((r) => setData(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <ClientLayout>
      <div className="mb-6">
        <h1 className="font-heading text-3xl text-[var(--brand)]">Portfolio</h1>
        <p className="mt-1 text-sm text-[var(--ink-muted)]">Live valuation on real AMFI NAVs · FIFO lot accounting · XIRR analytics</p>
      </div>
      {loading ? <div className="py-16 text-center text-sm text-[var(--ink-muted)]">Computing portfolio...</div> : (
        <>
          <SummaryCards s={data?.summary} />
          <HealthCard />
          <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
            <AllocationDonut allocation={data?.allocation} />
            <CategoryBars allocation={data?.allocation} total={data?.summary?.current_value} />
          </div>
          <Tabs defaultValue="holdings">
            <TabsList>
              <TabsTrigger value="holdings" data-testid="pf-tab-holdings">Holdings ({data?.holdings?.length ?? 0})</TabsTrigger>
              <TabsTrigger value="gains" data-testid="pf-tab-gains">Capital Gains</TabsTrigger>
            </TabsList>
            <TabsContent value="holdings">
              <Card className="border-[var(--glass-border)]"><CardContent className="p-0"><HoldingsTable holdings={data?.holdings || []} /></CardContent></Card>
            </TabsContent>
            <TabsContent value="gains"><CapitalGains /></TabsContent>
          </Tabs>
          <ExternalHoldings />
        </>
      )}
    </ClientLayout>
  );
}
